/* Imports */
import { defineStore } from 'pinia';
import { getAndUpText } from '@garmin/locale-and-up-text';
import { useGlobalStore } from './global.js';
import { useCustomerGroupsStore } from './customerGroups.js';

const defaultPricingData = Object.freeze({
  listPrice: null,
  salePrice: null,
  savings: null,
  unformatted: {
    listPrice: null,
    salePrice: null,
    savings: null,
  },
  andUpText: null,
});

export const usePricingStore = defineStore('pricing', {
  state: () => ({
    pricesLoading: {}, // loader per PID
    pricingData: [],
    fetchedPids: new Set(), // Simple tracking of processed PIDs
  }),
  getters: {
    getPricingData: (state) => state.pricingData,
    getIsPriceLoading: (state) => (pid) => Boolean(state.pricesLoading[pid]),
    getProductPricingByPid: (state) => (pid) => state.pricingData?.find((priceObj) => priceObj?.id === pid),
  },
  actions: {
    setPriceLoading(pid, loading) {
      this.pricesLoading[pid] = loading;
    },
    setPricingData(payload) {
      this.pricingData = payload;
    },
    formatPricingData(payload) {
      const globalStore = useGlobalStore();

      const derivePricingData = (priceData) => {
        // If there are no pricedSkus in the price data, bail out early with null pricing data object
        if (!priceData?.pricedSkus) {
          return defaultPricingData;
        }

        // We need to get a list of all the sku prices to determine if they are all the same price
        // Use the salePrice if available, otherwise use the listPrice
        const skuPrices = priceData.pricedSkus.map((skuPriceData) => (skuPriceData?.salePrice?.price ?? skuPriceData?.listPrice?.price ?? null));

        const filterOutNullValues = (price) => price;
        let andUpText = null;
        // If there are any variations in the related sku prices, we'll add "and up" text
        if (skuPrices.filter(filterOutNullValues).some((price) => price.toString() !== skuPrices[0].toString())) {
          andUpText = getAndUpText(globalStore.getLocale);
        }

        const formatSkuPrices = (skuPriceData) => ({
          listPrice: skuPriceData?.listPrice?.formattedPrice ?? null,
          salePrice: skuPriceData?.salePrice?.formattedPrice ?? null,
          savings: skuPriceData?.savings?.formattedPrice ?? null,
          sku: skuPriceData?.partNumber ?? null,
          unformatted: {
            listPrice: skuPriceData?.listPrice?.price ?? null,
            salePrice: skuPriceData?.salePrice?.price ?? null,
            savings: skuPriceData?.savings?.price ?? null,
          },
        });

        const consolidateSkuPrices = (prev, current) => {
          // Use salePrice if it exists, otherwise, use the listPrice
          const currentPrice = current?.unformatted?.salePrice ? current.unformatted.salePrice : current?.unformatted?.listPrice;
          const prevPrice = prev?.unformatted?.salePrice ? prev?.unformatted?.salePrice : prev?.unformatted?.listPrice;

          // Consolidate the sku prices to the lowest price
          return prevPrice < currentPrice ? prev : current;
        };

        const formattedPricedSkus = priceData.pricedSkus
          .map(formatSkuPrices)
          .reduce(consolidateSkuPrices);

        return {
          id: priceData.pid,
          andUpText,
          ...formattedPricedSkus,
        };
      };

      const formattedPricingData = payload.map((result) => {
        // If the promise for fetching resolved successfully,
        // the result will contain a value object with the pricing API response data
        if (result.value) {
          return derivePricingData(result.value);
        }
        // If the promise for fetching rejected (e.g. errored out)
        // the result will not contain a value object with the pricing API response data
        // and we will supply a null data pricing object to use on the front-end
        return defaultPricingData;
      });

      return formattedPricingData;
    },
    async fetchPricingData(productId) {
      const globalStore = useGlobalStore();
      const customerGroupsStore = useCustomerGroupsStore();

      const queryParams = new URLSearchParams();
      queryParams.append('locale', globalStore.getLocale);
      /**
       * If there are multiple customerGroups, they will be divided by a |
       * new URLSearchParams() will convert | to %7C which the pricing APIs expect when there are multiple customer groups
       */
      queryParams.append('customerGroup', customerGroupsStore.getCustomerGroups);

      const locationHref = window.location.href;
      if (locationHref.includes('cdncache=false')) queryParams.append('cdncache', false);
      // eslint-disable-next-line max-len
      const requestUrl = `${globalStore.getPricingProxyServicesBaseUrl}/countries/${globalStore.getCountryCode}/pid/${productId}/prices?${queryParams}`;

      try {
        /**
         * We need to send cookies with this request to pass along the GarminBuyReferrer cookie
         * Because of this, we are using fetch with credentials: 'include' instead of axios
         * axios with the withCredentials: true setting is not correctly sending cookies
         * with the request. More info here: https://github.com/axios/axios/issues/2149
         */
        const response = await fetch(requestUrl, {
          method: 'get',
          credentials: 'include',
        });

        // If the response has no content (HTTP 204), return null to avoid parsing errors
        if (response?.status === 204) return null;

        const priceData = await response.json();
        return priceData;
      } catch (error) {
        throw new Error(`Error encountered in Pricing - ${error.message}`, { cause: error });
      }
    },
    async fetchPrices() {
      const globalStore = useGlobalStore();
      const customerGroupsStore = useCustomerGroupsStore();

      await customerGroupsStore.setCustomerGroups();

      const products = globalStore.getProductData?.products;
      const productIds = Array.isArray(products) && products.map((product) => product.pid);

      if (!productIds.length) {
        // If there are no productIds, set the pricingData to an empty array
        this.setPricingData([]);
      } else {
        // Mark all PIDs as fetched to prevent duplicates + set loader
        productIds.forEach((pid) => {
          this.fetchedPids.add(pid);
          this.setPriceLoading(pid, true);
        });

        const promises = productIds.map((productId) => this.fetchPricingData(productId));
        const results = await Promise.allSettled([...promises]);

        // Turn off loaders
        productIds.forEach((pid) => this.setPriceLoading(pid, false));

        const formattedPricingData = this.formatPricingData(results);
        this.setPricingData(formattedPricingData);
      }
    },
    async fetchPriceForProduct(pid) {
      // Early return do not fetch if not needed
      if (this.fetchedPids.has(pid)) return;

      // Mark as fetched immediately
      this.fetchedPids.add(pid);

      this.setPriceLoading(pid, true);

      try {
        const priceData = await this.fetchPricingData(pid);
        const formattedPrice = this.formatPricingData([{ value: priceData }]);
        this.setPricingData([...this.getPricingData, ...formattedPrice]);
      } catch {
        throw new Error(`Pricing fetch failed for PID: ${pid}`);
      } finally {
        this.setPriceLoading(pid, false);
      }
    },
  },
});

export default usePricingStore;
